import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Edit2, Plus, Link as LinkIcon, Calendar } from 'lucide-react';

const StaffPage = ({ isAdmin }: { isAdmin: boolean }) => {
  const [employees, setEmployees] = useState<any[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Updated State: Added joining_date and leaving_date
  const [formData, setFormData] = useState({ 
    id: 0, 
    name: '', 
    designation: '', 
    username: '', 
    password: '', 
    joining_date: '', 
    leaving_date: '' 
  });

  const fetchStaff = async () => {
    try {
      // const res = await axios.get('https://attendance.nadezhda.in/employees');
      const res = await axios.get('/api/employees');
      setEmployees(res.data);
    } catch (err) {
      console.error("Error fetching staff:", err);
    }
  };

  useEffect(() => { fetchStaff(); }, []);

  const handleCopyLink = (id: number) => {
    const url = `${window.location.origin}/mark-attendance/${id}`;
    navigator.clipboard.writeText(url);
    alert("Link Copied! Send this to staff.");
  };

  const saveStaff = async () => {
    try {
      if (formData.id === 0) {
        await axios.post('/api/employees/add', formData);
      } else {
        await axios.put(`/api/employees/${formData.id}`, formData);
      }
      setIsModalOpen(false);
      fetchStaff();
    } catch (err) {
      alert("Error saving staff details");
      console.error(err);
    }
  };

  // Helper to format date for display
  const formatDate = (dateString: string) => {
    if (!dateString) return '---';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  return (
    <div className="p-4">
      <div className="flex justify-between mb-6">
        <h2 className="text-2xl font-black">Staff Accounts</h2>
        <button 
          onClick={() => {
            setFormData({
              id: 0, name: '', designation: '', username: '', password: '', 
              joining_date: new Date().toISOString().split('T')[0], // Default Joining = Today
              leaving_date: '' 
            }); 
            setIsModalOpen(true);
          }} 
          className="bg-blue-600 text-white px-4 py-2 rounded-xl flex items-center gap-2 hover:bg-blue-700 transition"
        >
          <Plus size={18}/> Add Staff
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {employees.map(emp => (
          <div key={emp.id} className={`bg-white p-5 rounded-2xl shadow-sm border flex justify-between items-center ${emp.leaving_date ? 'border-red-200 bg-red-50/30' : 'border-slate-100'}`}>
            <div>
              <p className="font-bold text-slate-800 flex items-center gap-2">
                {emp.name}
                {emp.leaving_date && <span className="text-[10px] bg-red-100 text-red-600 px-2 py-0.5 rounded-full">LEFT</span>}
              </p>
              <p className="text-xs text-slate-400 uppercase font-bold">{emp.designation}</p>
              
              <div className="mt-2 text-[10px] text-slate-500 font-mono flex flex-col gap-1">
                <span className="flex items-center gap-1"><Calendar size={10}/> Joined: {formatDate(emp.joining_date)}</span>
                {emp.leaving_date && <span className="text-red-400 flex items-center gap-1"><Calendar size={10}/> Left: {formatDate(emp.leaving_date)}</span>}
              </div>
              
              <p className="text-[10px] text-blue-500 mt-2 font-mono bg-blue-50 w-fit px-2 py-1 rounded">User: {emp.username || '---'}</p>
            </div>
            
            <div className="flex flex-col gap-2">
              <button onClick={() => handleCopyLink(emp.id)} className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg border border-emerald-100"><LinkIcon size={18}/></button>
              <button 
                onClick={() => {
                  setFormData({
                    ...emp, 
                    joining_date: emp.joining_date || '', // Handle nulls
                    leaving_date: emp.leaving_date || '' 
                  }); 
                  setIsModalOpen(true);
                }} 
                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg border border-blue-100"
              >
                <Edit2 size={18}/>
              </button>
            </div>
          </div>
        ))}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white p-6 rounded-3xl w-full max-w-md shadow-2xl">
            <h3 className="text-lg font-black mb-4 text-slate-700">Manage Staff Details</h3>
            
            <div className="space-y-3">
              <div>
                <label className="text-xs font-bold text-slate-400 uppercase ml-1">Full Name</label>
                <input placeholder="e.g. Rahul Kumar" value={formData.name} onChange={e=>setFormData({...formData, name:e.target.value})} className="w-full border p-3 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>
              
              <div>
                <label className="text-xs font-bold text-slate-400 uppercase ml-1">Designation</label>
                <input placeholder="e.g. Developer" value={formData.designation} onChange={e=>setFormData({...formData, designation:e.target.value})} className="w-full border p-3 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>

              {/* NEW: Date Fields */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-emerald-600 uppercase ml-1">Joining Date</label>
                  <input type="date" value={formData.joining_date} onChange={e=>setFormData({...formData, joining_date:e.target.value})} className="w-full border p-3 rounded-xl bg-emerald-50/50 focus:ring-2 focus:ring-emerald-500 outline-none" />
                </div>
                <div>
                  <label className="text-xs font-bold text-red-500 uppercase ml-1">Leaving Date</label>
                  <input type="date" value={formData.leaving_date} onChange={e=>setFormData({...formData, leaving_date:e.target.value})} className="w-full border p-3 rounded-xl bg-red-50/50 focus:ring-2 focus:ring-red-500 outline-none" />
                </div>
              </div>

              <div className="bg-slate-50 p-4 rounded-2xl space-y-3 border border-slate-100 mt-2">
                <p className="text-[10px] font-bold text-slate-400">LOGIN CREDENTIALS</p>
                <input placeholder="Username" value={formData.username} onChange={e=>setFormData({...formData, username:e.target.value})} className="w-full border p-3 rounded-xl bg-white focus:ring-2 focus:ring-blue-500 outline-none" />
                <input placeholder="Password" value={formData.password} onChange={e=>setFormData({...formData, password:e.target.value})} className="w-full border p-3 rounded-xl bg-white focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setIsModalOpen(false)} className="text-slate-500 font-bold hover:bg-slate-100 px-4 py-2 rounded-xl transition">Cancel</button>
              <button onClick={saveStaff} className="bg-blue-600 text-white px-6 py-2 rounded-xl font-bold hover:bg-blue-700 shadow-lg shadow-blue-200 transition">Save Details</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffPage;